<template>
  <button
    :class="classes"
    :disabled="disabled"
    @click="handleClick"
  >
    <BaseLoading v-if="props.loading" :hide-text="true" :on-primary="true"/>
    <template v-else>
      <component :is="iconLeft" v-if="props.iconLeft" class="h-5 w-5" />
      <slot />
    </template>
  </button>
</template>

<script setup lang="ts">
import { computed, type Component } from 'vue';
import BaseLoading from './BaseLoading.vue';

const emit = defineEmits<{
  (e: 'click', event: Event): void
}>()

const props = withDefaults(
  defineProps<{
    variant?: 'primary' | 'secondary' | 'danger' | 'brand'
    size?: 'sm' | 'md' | 'lg'
    disabled?: boolean;
    loading?: boolean;
    iconLeft?: Component;
  }>(),
  {
    variant: 'primary',
    size: 'md',
    disabled: false,
    iconLeft: undefined,
  }
)

const classes = computed<string[]>(() => {
  const BASE_CLASS = 'base-button';
  const tmp: string[] = ['base-button'];
  
  if (props.variant) tmp.push(`${BASE_CLASS}--variant-${props.variant}`);
  if (props.size) tmp.push(`${BASE_CLASS}--size-${props.size}`);
  if (props.disabled) tmp.push(`${BASE_CLASS}--disabled`);
  if (props.loading) tmp.push(`${BASE_CLASS}--loading`);

  return tmp;
});

function handleClick(e: Event) {
  if (props.disabled || props.loading) {
    e.preventDefault();
    return;
  }
  emit('click', e);
  return e;
}
</script>

<style>
@reference "@/assets/main.css";

.base-button {
  @apply rounded-lg font-medium focus:outline-hidden focus:ring-2 focus:ring-offset-2 flex gap-2 items-center cursor-pointer;
}

.base-button--size-sm {
  @apply px-3 py-1.5 text-sm;
}
.base-button--size-md {
  @apply px-4 py-2 text-sm;
}
.base-button--size-lg {
  @apply px-5 py-2.5 text-base;
}

.base-button--variant-primary {
  @apply bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500;
}
.base-button--variant-secondary {
  @apply bg-white text-neutral-700 border border-neutral-300 hover:bg-primary-200 focus:ring-primary-500;
}
.base-button--variant-danger {
  @apply bg-destructive-600 text-white hover:bg-destructive-700 focus:ring-destructive-500;
}
.base-button--variant-brand {
  @apply bg-[#205072] hover:bg-[#329C9C] focus:ring-[#329C9C] text-white text-center block w-full;
}

.base-button--disabled {
  @apply opacity-50 cursor-not-allowed;
}
.base-button--loading {
  @apply cursor-progress;
}
</style>